import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ChatDotsInactiveIcon, CreateConversationInactiveIcon } from '@getgo/chameleon-icons/react';
import { Button } from '@getgo/chameleon-web/react';
import classNames from 'classnames';
import NoItemsAvailable from "../NoItemsAvailable";
import { DateTime } from 'luxon';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConversationSource, isAgentAfterHoursEvent, isAssignEvent, isCallEvent, isCobrowseStartedEvent, isMeetingEvent, isMessageEvent, isParticipantLeftEvent, isResolveEvent, isUnassignEvent, isWebChatAfterHoursEvent, isWebChatEndedEvent, MessageDirection, } from "../../state/conversation/types";
import AgentAfterHoursMessageEvent from './AgentAfterHoursMessageEvent';
import AssignmentEvent from './AssignmentEvent';
import CallEvent from './CallEvent';
import ChatEndedEvent from './ChatEndedEvent';
import ChatMessageEvent from './ChatMessageEvent';
import CobrowseStartedEvent from './CobrowseStartedEvent';
import FacebookAlertEvent from './FacebookAlertEvent';
import classes from './index.module.scss';
import MeetingEvent from './MeetingEvent';
import NewMessageIndicator from './NewMessageIndicator';
import ParticipantLeftConversationEvent from './ParticipantLeftConversationEvent';
import ResolutionEvent from './ResolutionEvent';
import UnassignmentEvent from './UnassignmentEvent';
import WebChatAfterHoursMessageEvent from './WebChatAfterHoursMessageEvent';
const isSameGroup = (event, previousEvent) => {
    const difference = DateTime.fromISO(event.createdAt)
        .diff(DateTime.fromISO(previousEvent.createdAt), 'minutes')
        .toObject();
    return (event.direction === previousEvent.direction &&
        difference.minutes <= 0 &&
        difference.minutes >= -1);
};
const groupMessages = (conversationEvents) => {
    const groups = [];
    let previousEvent;
    let group = [];
    conversationEvents === null || conversationEvents === void 0 ? void 0 : conversationEvents.forEach(event => {
        if (isMessageEvent(event)) {
            if (previousEvent && !isSameGroup(event, previousEvent)) {
                group.length > 1 && groups.push(group);
                group = [];
            }
            group.push(event.id);
        }
        else {
            group = [];
        }
        previousEvent = event;
    });
    if (group.length > 1) {
        groups.push(group);
    }
    return groups;
};
const getParticipantWaitTime = (conversationEvents) => {
    var _a, _b;
    const isResolvedOrClosed = conversationEvents === null || conversationEvents === void 0 ? void 0 : conversationEvents.some(c => isResolveEvent(c) || isParticipantLeftEvent(c));
    const inboundMessage = (_b = (_a = conversationEvents === null || conversationEvents === void 0 ? void 0 : conversationEvents.filter(isMessageEvent)) === null || _a === void 0 ? void 0 : _a.filter(msg => (msg === null || msg === void 0 ? void 0 : msg.direction) === MessageDirection.INBOUND)) === null || _b === void 0 ? void 0 : _b[0];
    return !isResolvedOrClosed && inboundMessage
        ? (Date.now().valueOf() - new Date(inboundMessage.createdAt).valueOf()) / 1000
        : 0;
};
const getMessageGroup = (id, groups) => {
    let entry = null;
    groups.some(group => {
        const index = group.indexOf(id);
        if (index !== -1) {
            entry = {
                group,
                index,
            };
        }
        return index !== -1;
    });
    return entry;
};
const SCROLL_TO_BOTTOM_THRESHOLD = -150;
const ConversationEvents = ({ agents, className, color, compact, conversationId, currentUser, customAvatarIconUrl, events, fileBlobsWithId, fullWidth, genericAvatarIconId, getFileBlob, isWebChat, lastMessageReadAt, onAssign, onReadIndicator, onRetry, participant, participantMetadataContactInformation, source, thumbnailSize, truncate = true, }) => {
    var _a;
    const [groups, setGroups] = useState(groupMessages(events));
    const { t } = useTranslation();
    const [showNewMessageIndicator, setShowNewMessageIndicator] = useState(false);
    const [participantWaitTime, setParticipantWaitTime] = useState(getParticipantWaitTime(events));
    useEffect(() => {
        setParticipantWaitTime(getParticipantWaitTime(events));
    }, [events]);
    useEffect(() => {
        const timer = setTimeout(() => {
            setParticipantWaitTime(getParticipantWaitTime(events));
        }, 30000);
        return () => clearTimeout(timer);
    });
    const messagesRef = useRef(null);
    const scrollToBottom = () => {
        if (messagesRef.current) {
            const scrollHeight = messagesRef.current.scrollHeight;
            const height = messagesRef.current.clientHeight;
            const maxScrollTop = scrollHeight - height;
            messagesRef.current.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
        }
    };
    const autoScrollToBottom = () => {
        var _a;
        if (((_a = messagesRef === null || messagesRef === void 0 ? void 0 : messagesRef.current) === null || _a === void 0 ? void 0 : _a.scrollTop) >= SCROLL_TO_BOTTOM_THRESHOLD) {
            scrollToBottom();
        }
        else {
            setShowNewMessageIndicator(true);
        }
    };
    useEffect(() => {
        var _a, _b;
        setGroups(groupMessages(events));
        autoScrollToBottom();
        if (((_a = messagesRef === null || messagesRef === void 0 ? void 0 : messagesRef.current) === null || _a === void 0 ? void 0 : _a.scrollTop) >= 0) {
            onReadIndicator === null || onReadIndicator === void 0 ? void 0 : onReadIndicator();
        }
        const handleScroll = () => {
            // chat window was scrolled all the way down
            var _a, _b;
            if (((_a = messagesRef === null || messagesRef === void 0 ? void 0 : messagesRef.current) === null || _a === void 0 ? void 0 : _a.scrollTop) >= 0) {
                onReadIndicator === null || onReadIndicator === void 0 ? void 0 : onReadIndicator();
            }
            if (((_b = messagesRef === null || messagesRef === void 0 ? void 0 : messagesRef.current) === null || _b === void 0 ? void 0 : _b.scrollTop) >= SCROLL_TO_BOTTOM_THRESHOLD) {
                setShowNewMessageIndicator(false);
            }
        };
        (_b = messagesRef === null || messagesRef === void 0 ? void 0 : messagesRef.current) === null || _b === void 0 ? void 0 : _b.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            var _a;
            (_a = messagesRef === null || messagesRef === void 0 ? void 0 : messagesRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('scroll', handleScroll);
        };
    }, [events, onReadIndicator]);
    const handleNewMessageIndicatorClick = () => {
        scrollToBottom();
        setShowNewMessageIndicator(false);
    };
    const lastOutboundReadMessageId = (_a = events === null || events === void 0 ? void 0 : events.find(event => isMessageEvent(event) &&
        event.direction === MessageDirection.OUTBOUND &&
        new Date(lastMessageReadAt).getTime() >= new Date(event.createdAt).getTime())) === null || _a === void 0 ? void 0 : _a.id;
    return (_jsxs("div", Object.assign({ ref: messagesRef, className: classNames(classes.messages, className), role: "list", "aria-label": "messages" }, { children: [showNewMessageIndicator && _jsx(NewMessageIndicator, { onClick: handleNewMessageIndicatorClick }, void 0), (source === ConversationSource.FACEBOOK || source === ConversationSource.INSTAGRAM) && (_jsx(FacebookAlertEvent, { compact: compact, fullWidth: fullWidth, participantWaitTime: participantWaitTime, source: source }, void 0)), onAssign && (_jsx(NoItemsAvailable, { icon: _jsx(ChatDotsInactiveIcon, {}, void 0), body: t('Assign conversation or send a message'), title: t('This conversation is unassigned'), actions: [
                    _jsx(Button, Object.assign({ variant: "primary", leadingIcon: _jsx(CreateConversationInactiveIcon, {}, void 0), onClick: () => onAssign() }, { children: t('Assign conversation') }), "assign-conversation"),
                ] }, void 0)), events === null || events === void 0 ? void 0 : events.map(event => isMessageEvent(event) ? (_jsx(ChatMessageEvent, { agents: agents, color: color, compact: compact, conversationId: conversationId, currentUser: currentUser, customAvatarIconUrl: customAvatarIconUrl, entry: getMessageGroup(event.id, groups), fileBlobsWithId: fileBlobsWithId, fullWidth: fullWidth, genericAvatarIconId: genericAvatarIconId, getFileBlob: getFileBlob, isLastOutboundReadMessageEvent: lastOutboundReadMessageId === event.id, isWebChat: isWebChat, lastMessageReadAt: lastMessageReadAt, message: event, onSend: onRetry, participant: participant, participantMetadataContactInformation: participantMetadataContactInformation, thumbnailSize: thumbnailSize }, event.id)) : isAssignEvent(event) ? (_jsx(AssignmentEvent, { compact: compact, fullWidth: fullWidth, currentUserId: currentUser.id, assign: event, truncate: truncate }, event.id)) : isUnassignEvent(event) ? (_jsx(UnassignmentEvent, { compact: compact, fullWidth: fullWidth, currentUserId: currentUser.id, unassign: event, truncate: truncate }, event.id)) : isAgentAfterHoursEvent(event) ? (_jsx(AgentAfterHoursMessageEvent, { compact: compact, event: event, participantMetadataContactInformation: participantMetadataContactInformation, participant: participant, fullWidth: fullWidth, truncate: truncate }, event.id)) : isResolveEvent(event) ? (_jsx(ResolutionEvent, { compact: compact, fullWidth: fullWidth, currentUserId: currentUser.id, by: event.resolvedBy, resolve: event, truncate: truncate }, event.id)) : isMeetingEvent(event) ? (_jsx(MeetingEvent, { compact: compact, meeting: event, fullWidth: fullWidth, truncate: truncate }, event.id)) : isCallEvent(event) ? (_jsx(CallEvent, { compact: compact, call: event, fullWidth: fullWidth, truncate: truncate }, event.id)) : isParticipantLeftEvent(event) ? (_jsx(ParticipantLeftConversationEvent, { compact: compact, event: event, fullWidth: fullWidth, participantMetadataContactInformation: participantMetadataContactInformation, participant: participant, truncate: truncate }, event.id)) : isCobrowseStartedEvent(event) ? (_jsx(CobrowseStartedEvent, { compact: compact, fullWidth: fullWidth, cobrowse: event, color: color, agents: agents, customAvatarIconUrl: customAvatarIconUrl, genericAvatarIconId: genericAvatarIconId, entry: getMessageGroup(event.id, groups), currentUser: currentUser, truncate: truncate }, event.id)) : isAgentAfterHoursEvent(event) ? (_jsx(AgentAfterHoursMessageEvent, { compact: compact, event: event, fullWidth: fullWidth, participantMetadataContactInformation: participantMetadataContactInformation, participant: participant, truncate: truncate }, event.id)) : isWebChatAfterHoursEvent(event) ? (_jsx(WebChatAfterHoursMessageEvent, { compact: compact, fullWidth: fullWidth, color: color, event: event }, event.id)) : isWebChatEndedEvent(event) ? (_jsx(ChatEndedEvent, { compact: compact, fullWidth: fullWidth, color: color }, event.id)) : null)] }), void 0));
};
export default ConversationEvents;
//# sourceMappingURL=index.js.map